import type { Memory } from '@ralph/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { confirmMemory, createMemory, deleteMemory, fetchMemories, updateMemory } from '@/api/memories.ts'

/**
 * The Memory page's data.
 *
 * The query key is deliberately the same `['memories']` that `use-api.ts` uses: both read
 * the same endpoint, and two keys would mean a confirm here left a second cached copy of the
 * same list stale somewhere else. This module supersedes the four memory hooks in
 * `use-api.ts` (the page is their only caller); those stay until the coordinator prunes
 * them, because that file is shared with other work in flight.
 */

export const memoryKeys = { all: ['memories'] as const }

export const useMemoryRows = () => useQuery({ queryKey: memoryKeys.all, queryFn: fetchMemories })

export const useCreateMemoryRow = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { category: Memory['category']; statement: string }) => createMemory(input),
    onSuccess: () => client.invalidateQueries({ queryKey: memoryKeys.all })
  })
}

export const useUpdateMemoryRow = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; enabled?: boolean; statement?: string }) => updateMemory(id, patch),
    onSuccess: () => client.invalidateQueries({ queryKey: memoryKeys.all })
  })
}

export const useDeleteMemoryRow = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteMemory(id),
    onSuccess: () => client.invalidateQueries({ queryKey: memoryKeys.all })
  })
}

/** Confirming a suggestion is its own act, so it is its own hook (AGM-091). */
export const useConfirmMemory = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => confirmMemory(id),
    onSuccess: () => client.invalidateQueries({ queryKey: memoryKeys.all })
  })
}
